import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Props as ReactSelectProps, components } from 'react-select';
import Tooltip from 'components/Tooltip/Tooltip';
import {
  Label,
  Select as SelectStyles,
  ErrorMessage,
  CheckboxWrapper,
  CheckboxLable,
  SingleValue as SingleValueStyles,
  ClearIndicatorContainer,
  SearchIndicatorContainer,
} from './SelectStyles';
import { CheckInput } from 'components/form/Check';
import AsyncSelect from 'react-select/async';
import { Api } from 'api';
import { useDataEnvironment } from 'contexts/DataEnvironmentContext';
import { debounce } from 'lodash';
import { ReturnComponent } from 'types/ReturnComponent';
import { useHandleSearchInput } from './handlers/useHandleSearchInput';
import { zIndex } from 'helpers/constants';
import { Search, X } from 'react-bootstrap-icons';

const customStyles = ({
  size,
  styles,
  withCheckboxes = false,
  hasError,
}: any) => ({
  ...styles,
  control: (provided: any, state: any) => {
    const borderColor = state.isDisabled ? '#D1D1D1' : '#c3cfd9';
    const bgColor = state.isDisabled ? '#E6E6E6' : '#fff';
    const heightSize: any = { sm: 24, md: 30, lg: 30 };

    return {
      ...provided,
      ...styles?.control,
      height: heightSize[size] || 30,
      minHeight: heightSize[size] || 30,
      border: `1px solid ${hasError ? '#FD0F0F' : borderColor}`,
      boxShadow: hasError
        ? '2px 2px 0px #FFD6D7, -2px 2px 0px #FFD6D7, 2px -2px 0px #FFD6D7, -2px -2px 0px #FFD6D7, inset 0px 2px 2px rgba(0, 0, 0, 0.08)'
        : 'none',
      padding: '0',
      outline: 'none',
      background: bgColor,
      cursor: 'pointer',
      overflow: 'hidden',
      borderRadius: '3px',
    };
  },
  input: (provided: any, state: any) => {
    const fontSize: any = { sm: '12px', md: '14px', lg: '14px' };
    return {
      ...provided,
      ...styles?.input,
      gridArea: '1 / 1 / 2 / 3',
      // disables auto width (expanding)
      gridTemplateColumns: '0 auto auto',
      fontSize,
      color: '#1D1E25',
      fontFamily: 'Roboto',
      margin: 0,
      padding: 0,
    };
  },
  dropdownIndicator: (provided: any, state: any) => ({
    ...provided,
    ...styles?.dropdownIndicator,
    visibility: 'hidden',
    padding: '0 13px',
    svg: {
      width: '13px',
      fill: '#8795A2',
    },
  }),
  indicatorSeparator: () => ({
    ...styles?.indicatorSeparator,
    display: 'none',
  }),
  valueContainer: (provided: any, state: any) => ({
    ...styles?.valueContainer,
    ...provided,
    paddingTop: 0,
    paddingBottom: 0,
    maxHeight: '22px',
  }),
  option: (provided: any, state: any) => {
    const st = withCheckboxes ? { backgroundColor: 'transparent' } : {};

    return {
      ...styles?.option,
      ...provided,
      ...st,
      cursor: 'pointer',
    };
  },
  multiValueRemove: (provided: any, state: any) => {
    return {
      ...styles?.option,
      ...provided,
      ':hover': null,
    };
  },
  menuList: (provided: any) => ({
    ...provided,
    overflowX: 'hidden',
  }),
  menuPortal: (provided: any) => ({
    ...provided,
    zIndex: zIndex.menuList,
  }),
});

const Option = (props: any) => {
  return (
    <div>
      <components.Option {...props}>
        <CheckboxWrapper>
          <CheckInput register={{ checked: props.isSelected }} />
          <CheckboxLable title={props.label}>{props.label}</CheckboxLable>
        </CheckboxWrapper>
      </components.Option>
    </div>
  );
};

const MultiValue = (props: any) => {
  if (props.index > 0) return null;
  const { length } = props.getValue();
  let label = `${length} selected`;
  if (length === props.options.length) {
    label = 'All';
  }
  return <div>{label}</div>;
};

const SingleValue = (props: any) => {
  const { singleValueTextRef, singleValueRef, getValue } = props;
  const value = getValue();

  if (value[0]) {
    return (
      <SingleValueStyles ref={singleValueRef}>
        <span ref={singleValueTextRef}>{value[0].label}</span>
      </SingleValueStyles>
    );
  }

  return <SingleValueStyles></SingleValueStyles>;
};

type Props = {
  isTree?: boolean;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  isPortalled?: boolean;
  size?: 'sm' | 'md' | 'lg';
  errorMessage?: string;
  style?: any;
  withCheckboxes?: boolean;
  hasError?: boolean;
  onChange: (selectedItem: any, actionMeta?: any) => void;
  onBlur: (e: any) => void;
  testid?: string;
  isLoading?: boolean;
  filter?: 'pullforward' | 'weighting' | undefined;
  searchInputValue?: string;
  onSearchInputChange?: (value: string) => void;
} & Omit<ReactSelectProps, 'isDisabled' | 'onChange'>;

export const ReturnComponentSearch: React.FC<Props> = ({
  isTree = false,
  label,
  required = false,
  disabled = false,
  isPortalled,
  size = 'md',
  errorMessage,
  hasError,
  styles,
  style,
  withCheckboxes,
  testid,
  filter,
  onChange,
  onBlur,
  searchInputValue,
  onSearchInputChange,
  ...otherProps
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const singleValueTextRef = useRef<any>(null);
  const singleValueRef = useRef<any>(null);
  const { value: values }: any = otherProps;
  const value = values && values[0] ? values[0]?.label : values?.label;
  const [defaultOptions, setDefaultOptions] = useState<Array<any>>([]);
  const [inputValue, setInputValue] = useState('');
  const { isInputValid } = useHandleSearchInput({
    errorMessage,
  });
  const [hasSearched, setHasSearched] = useState(false);

  const [dataEnvironment] = useDataEnvironment();
  const DEBOUNCE_VALUE = 500;

  const config = withCheckboxes
    ? {
        components: { Option, MultiValue },
        isMulti: true,
        closeMenuOnSelect: false,
        hideSelectedOptions: false,
        isClearable: false,
      }
    : isTree
    ? {}
    : {
        components: {
          SingleValue: (props: any) => (
            <SingleValue
              {...props}
              singleValueTextRef={singleValueTextRef}
              singleValueRef={singleValueRef}
            />
          ),
        },
      };

  const isShowTooltip = () => {
    const w1 = singleValueTextRef?.current?.offsetWidth;
    const w2 = singleValueRef?.current?.offsetWidth;

    return w1 > w2;
  };

  function changeInputValue(val: string) {
    onSearchInputChange?.(val) ?? setInputValue(val);
  }

  useEffect(() => {
    if (value) {
      changeInputValue('');
      setShowTooltip(isShowTooltip());
      if (!hasSearched) {
        setDefaultOptions([values]);
      }
    }
  }, [value]);

  const abortController = useRef<AbortController>();

  function loadOptions(input: string, callback: (options: any[]) => void) {
    abortController.current?.abort();
    abortController.current = new AbortController();
    queryOptions(input, callback, abortController.current);
  }
  const queryOptions = useCallback(
    debounce(
      (
        input: string,
        callback: (options: any[]) => void,
        abortController: AbortController
      ) => {
        if (input.trim().length > 0) {
          const valid = isInputValid(input);
          if (!valid) {
            callback([]);
            setHasSearched(true);
            return;
          }
          const dataEnvId = dataEnvironment?.id?.toString() ?? '';
          Api.lookup
            .GetReturnComponentsQuery(dataEnvId, input, abortController)
            .then((resp: any) => {
              const data = resp.data ?? [];
              let options = data;
              if (filter === 'pullforward') {
                options = options.filter(
                  (data: ReturnComponent) =>
                    data.benchmarkType?.name !== 'Pull Forward'
                );
              }
              if (filter === 'weighting') {
                options = options.filter(
                  (data: ReturnComponent) => data.resourceId
                );
              }
              options = options.map((d: any) => {
                return {
                  ...d,
                  label: d.name,
                  value: d.resourceId?.toString() || d.benchmarkId,
                };
              });
              setDefaultOptions(options);
              callback(options);
              setHasSearched(true);
            })
            .catch((e: any) => {
              callback([]);
            });
        } else {
          callback([]);
        }
      },
      DEBOUNCE_VALUE
    ),
    []
  );

  let errorProps: any = {};
  if (testid) {
    errorProps['data-testid'] = `${testid}-error-message`;
  }

  function handleKeyDown(event: any) {
    switch (event.key) {
      case 'Home':
        event.preventDefault();
        if (event.shiftKey) event.target.selectionStart = 0;
        else event.target.setSelectionRange(0, 0);
        break;
      case 'End':
        event.preventDefault();
        const len = event.target.value.length;
        if (event.shiftKey) event.target.selectionEnd = len;
        else event.target.setSelectionRange(len, len);
        break;
    }
  }

  const selectComponent = (
    <AsyncSelect
      defaultOptions={defaultOptions}
      loadOptions={loadOptions}
      menuPlacement='auto'
      menuPortalTarget={document.body}
      menuPosition='absolute'
      aria-label={testid}
      isDisabled={disabled}
      styles={customStyles({
        size,
        styles,
        hasError: hasError || errorMessage ? true : false,
        withCheckboxes,
      })}
      placeholder='Search for component'
      inputValue={searchInputValue ?? inputValue}
      onInputChange={(newValue, actionMeta) => {
        if (newValue.length > 255) return;
        if (actionMeta.action === 'input-change') {
          changeInputValue(newValue);
          if (!newValue) {
            abortController.current?.abort();
            onChange('', '');
            setDefaultOptions([]);
          }
        }
      }}
      onChange={(newValue: any, actionmeta) => {
        if (actionmeta.action === 'clear') {
          onChange('', '');
        } else {
          onChange(newValue, actionmeta);
        }
        changeInputValue('');
      }}
      onBlur={(e) => {
        if (e.target.value && e.target.value !== value) {
          onChange('', '');
        }
        onBlur?.(e);
      }}
      onMenuOpen={() => {
        if (value) changeInputValue(value);
      }}
      onKeyDown={handleKeyDown}
      noOptionsMessage={() => (
        <div>{hasSearched ? 'No results' : 'Type to search'}</div>
      )}
      {...otherProps}
      {...config}
    />
  );

  const clearButton = useMemo(() => {
    return inputValue || value ? (
      <ClearIndicatorContainer
        onClick={() => {
          changeInputValue('');
          onChange('', '');
        }}
      >
        <X size={20} />
      </ClearIndicatorContainer>
    ) : (
      <SearchIndicatorContainer>
        <Search />
      </SearchIndicatorContainer>
    );
  }, [inputValue, value, onChange]);

  return (
    <SelectStyles style={style} data-testid={testid}>
      {label && (
        <Label>
          {required && <span>*</span>}
          {label}
        </Label>
      )}
      {showTooltip ? (
        <Tooltip
          message={value || null}
          delay={300}
          followCursor={false}
          placement='top'
        >
          {selectComponent}
          {clearButton}
        </Tooltip>
      ) : (
        <>
          {selectComponent}
          {clearButton}
        </>
      )}
      {errorMessage && (
        <ErrorMessage {...errorProps}>{errorMessage}</ErrorMessage>
      )}
    </SelectStyles>
  );
};
